import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { acceptAll, rejectAll, updateConsent, readConsent } from '@/utils/consent';

export default function CookieBanner({ open, initialMode = 'banner', onClose }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState(initialMode); // 'banner' | 'prefs'
  const [prefs, setPrefs] = useState(() => {
    const saved = readConsent();
    return {
      functionality_storage: saved?.functionality_storage ?? true,
      analytics_storage: saved?.analytics_storage ?? false,
      ad_storage: saved?.ad_storage ?? false,
      ad_user_data: saved?.ad_user_data ?? false,
      ad_personalization: saved?.ad_personalization ?? false,
      personalization_storage: saved?.personalization_storage ?? false,
    };
  });

  useEffect(() => {
    if (open) setMode(initialMode);
  }, [open, initialMode]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleAcceptAll = () => {
    acceptAll();
    onClose?.();
  };
  const handleRejectAll = () => {
    rejectAll();
    onClose?.();
  };
  const handleSave = () => {
    updateConsent(prefs);
    onClose?.();
  };

  const toggleAds = (v) => {
    setPrefs((p) => ({
      ...p,
      ad_storage: v,
      ad_user_data: v,
      ad_personalization: v,
      personalization_storage: v,
    }));
  };

  return (
    <div className="aq-cookie-root" role="dialog" aria-modal="true" aria-label={t('cookies.banner_title')} data-testid="cookie-banner">
      <div className="aq-cookie-scrim" onClick={() => onClose?.()} />
      <div className={`aq-cookie ${mode === 'prefs' ? 'is-prefs' : ''}`}>
        {mode === 'banner' && (
          <>
            <div className="aq-cookie__body">
              <h3 className="aq-cookie__title">{t('cookies.banner_title')}</h3>
              <p className="aq-cookie__text">{t('cookies.banner_text')}</p>
            </div>
            <div className="aq-cookie__actions">
              <button type="button" className="aq-btn aq-btn--ghost" onClick={handleRejectAll} data-testid="cookie-reject">
                {t('cookies.reject_all')}
              </button>
              <button type="button" className="aq-btn aq-btn--outline" onClick={() => setMode('prefs')} data-testid="cookie-manage">
                {t('cookies.manage')}
              </button>
              <button type="button" className="aq-btn aq-btn--primary" onClick={handleAcceptAll} data-testid="cookie-accept">
                {t('cookies.accept_all')}
              </button>
            </div>
          </>
        )}

        {mode === 'prefs' && (
          <>
            <div className="aq-cookie__body">
              <h3 className="aq-cookie__title">{t('cookies.prefs_title')}</h3>

              <div className="aq-cookie__row">
                <div>
                  <h4>{t('cookies.cat_necessary_title')}</h4>
                  <p>{t('cookies.cat_necessary_text')}</p>
                </div>
                <span className="aq-badge">{t('cookies.required_badge')}</span>
              </div>

              <label className="aq-cookie__row">
                <div>
                  <h4>{t('cookies.cat_functional_title')}</h4>
                  <p>{t('cookies.cat_functional_text')}</p>
                </div>
                <span className="aq-switch">
                  <input
                    type="checkbox"
                    checked={prefs.functionality_storage}
                    onChange={(e) => setPrefs((p) => ({ ...p, functionality_storage: e.target.checked }))}
                    data-testid="cookie-toggle-functional"
                  />
                  <span className="aq-switch__ui" aria-hidden="true"></span>
                </span>
              </label>

              <label className="aq-cookie__row">
                <div>
                  <h4>{t('cookies.cat_analytics_title')}</h4>
                  <p>{t('cookies.cat_analytics_text')}</p>
                </div>
                <span className="aq-switch">
                  <input
                    type="checkbox"
                    checked={prefs.analytics_storage}
                    onChange={(e) => setPrefs((p) => ({ ...p, analytics_storage: e.target.checked }))}
                    data-testid="cookie-toggle-analytics"
                  />
                  <span className="aq-switch__ui" aria-hidden="true"></span>
                </span>
              </label>

              <label className="aq-cookie__row">
                <div>
                  <h4>{t('cookies.cat_ads_title')}</h4>
                  <p>{t('cookies.cat_ads_text')}</p>
                </div>
                <span className="aq-switch">
                  <input
                    type="checkbox"
                    checked={prefs.ad_storage}
                    onChange={(e) => toggleAds(e.target.checked)}
                    data-testid="cookie-toggle-ads"
                  />
                  <span className="aq-switch__ui" aria-hidden="true"></span>
                </span>
              </label>
            </div>
            <div className="aq-cookie__actions">
              <button type="button" className="aq-btn aq-btn--ghost" onClick={handleRejectAll} data-testid="cookie-reject-2">
                {t('cookies.reject_all')}
              </button>
              <button type="button" className="aq-btn aq-btn--outline" onClick={handleAcceptAll} data-testid="cookie-accept-2">
                {t('cookies.accept_all')}
              </button>
              <button type="button" className="aq-btn aq-btn--primary" onClick={handleSave} data-testid="cookie-save">
                {t('cookies.save')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
